import { incidentPath } from '@fos/shared/contracts/monitoring';
import { todayLocal, toLocalTime } from '@fos/shared/time';
import { Calendar, CircleCheck, Inbox, ShieldCheck, SquareCheckBig, TriangleAlert, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import {
  firstName,
  formatAge,
  formatDateShort,
  formatDayLong,
  formatDuration,
  formatMoneyAuto,
  formatMoneyTotal,
  formatTime,
  formatWeekday,
  greeting,
  lowerInitial,
  plural,
} from '../../../lib/format';
import { useAccess } from '../../../lib/useAccess';
import { ListRow } from '../../../ui/ListRow';
import { Page } from '../../../ui/Page';
import { PageHeader, SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Stat } from '../../../ui/Stat';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { Dot, Status } from '../../../ui/Status';
import { DaySummaryButton } from '../../copilot';
import { SEGMENTED_TOUCH, useIsPhone } from '../../mobile';
import {
  type DateRange,
  itemsByDay,
  monthRange,
  readStoredView,
  shiftAnchor,
  storeView,
  TODAY_VIEW_LABELS,
  TODAY_VIEWS,
  type TodayView,
  weekRange,
} from '../calendar';
import { DayPanel, MonthView, NeedsYouBar, RangeNav, WeekView } from '../components/CalendarViews';
import { OtherWorkspacesSection } from '../components/OtherWorkspaces';
import { PlanToday } from '../components/PlanToday';
import { QuickNotesSection } from '../components/QuickNotes';
import { EMPTY_TODAY, type TodaySummary, useTodayCalendar, useTodaySummary } from '../today-data';

/** Odświeża „teraz” co minutę (powitanie i data zmieniają się bez przeładowania). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

const VIEW_OPTIONS = TODAY_VIEWS.map((v) => ({ value: v, label: TODAY_VIEW_LABELS[v] }));

/**
 * Dziś (Figma 3018:1898) z przełącznikiem Dzień / Tydzień / Miesiąc (DECISIONS 2026-10-09; wybór pamiętany per
 * osoba w przeglądarce). Każdy widok ma inny cel:
 *  - Dzień („co teraz”): powitanie, „Podsumuj dzień” (Copilot), 5 kafelków, „Wymaga Ciebie” i „Plan na dziś”
 *    (spotkania dnia wg godzin + zadania na dziś i po terminie),
 *  - Tydzień („jak rozłożona praca”): jedna linijka podsumowania, 7 dni z małymi kafelkami, pasek „Wymaga Ciebie”,
 *  - Miesiąc („z góry”): mała siatka dni zabarwionych kolorem najważniejszej rzeczy i 3–4 liczby.
 */
export function TodayPage() {
  const session = useActiveSession();
  const access = useAccess();
  const now = useNow();
  const today = todayLocal(now);
  const summary = useTodaySummary();
  const data = summary.data ?? EMPTY_TODAY;
  // Bez danych po błędzie nie pokazujemy zer („Alarmy 0 · wszystko działa”), tylko jeden stan błędu.
  // Błąd odświeżenia przy wcześniej wczytanych danych: zostają ostatnie dane.
  const failed = summary.isError && !summary.data;
  const retry = () => void summary.refetch();

  const userId = session.user.id;
  const [view, setViewState] = useState<TodayView>(() => readStoredView(userId));
  const [anchor, setAnchor] = useState(today);
  const [selected, setSelected] = useState<string | null>(null);
  const setView = (next: TodayView) => {
    setViewState(next);
    storeView(userId, next);
    setSelected(null);
  };

  return (
    <Page>
      <PageHeader
        title={`${greeting(now)}, ${firstName(session.user.name)}`}
        aside={
          <Segmented
            aria-label="Widok"
            value={view}
            onValueChange={setView}
            options={VIEW_OPTIONS}
            className={SEGMENTED_TOUCH}
          />
        }
        meta={<TodayMeta date={now} data={summary.data} canApprovals={access.can('approvals')} />}
        actions={<DaySummaryButton />}
      />

      {view === 'day' ? (
        failed ? (
          <Section className="mt-6">
            <ErrorState onRetry={retry} retrying={summary.isFetching} />
          </Section>
        ) : (
          <>
            {/* Desktop: kafelki ≥ 180 px (przy 1024 px układ 3 + 2 zamiast 5 ściśniętych kolumn), mobile: 2 kolumny. */}
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
              {summary.isPending ? (
                Array.from({ length: 5 }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: szkielet
                  <Skeleton key={i} className="h-[118px] rounded-xl" />
                ))
              ) : (
                <TodayStats data={data} now={now} />
              )}
            </div>
            <TodayLists data={data} pending={summary.isPending} today={today} now={now} />
          </>
        )
      ) : (
        <RangeViews
          view={view}
          anchor={anchor}
          setAnchor={setAnchor}
          today={today}
          selected={selected}
          setSelected={setSelected}
          summary={summary.data}
          onShowDay={() => setView('day')}
        />
      )}
      {/* 17.10.2026: karteczki — to samo miejsce w widokach Dzień / Tydzień / Miesiąc, pod treścią widoku */}
      <QuickNotesSection className="mt-4 md:mt-6" />
      <OtherWorkspacesSection />
    </Page>
  );
}

/** Tydzień i Miesiąc: zakres od kotwicy, strzałki poprzedni / następny, lista dnia po kliknięciu dnia. */
function RangeViews({
  view,
  anchor,
  setAnchor,
  today,
  selected,
  setSelected,
  summary,
  onShowDay,
}: {
  view: 'week' | 'month';
  anchor: string;
  setAnchor: (date: string) => void;
  today: string;
  selected: string | null;
  setSelected: (date: string | null) => void;
  summary: TodaySummary | undefined;
  onShowDay: () => void;
}) {
  const range: DateRange = view === 'week' ? weekRange(anchor) : monthRange(anchor);
  const calendar = useTodayCalendar(range);
  const containsToday = range.from <= today && today <= range.to;
  const move = (step: -1 | 1) => {
    setAnchor(shiftAnchor(view, range.from, step));
    setSelected(null);
  };
  const nav = (
    <RangeNav
      unit={view === 'week' ? 'tydzień' : 'miesiąc'}
      onPrev={() => move(-1)}
      onNext={() => move(1)}
      onToday={
        containsToday
          ? undefined
          : () => {
              setAnchor(today);
              setSelected(null);
            }
      }
    />
  );
  const viewProps = {
    range,
    data: calendar.data,
    pending: calendar.isPending,
    failed: calendar.isError && !calendar.data,
    onRetry: () => void calendar.refetch(),
    retrying: calendar.isFetching,
    today,
    selected,
    onSelect: (date: string) => setSelected(selected === date ? null : date),
    nav,
  };
  const selectedInRange = selected && selected >= range.from && selected <= range.to ? selected : null;

  return (
    <div className="mt-6 flex flex-col gap-4 md:gap-6">
      {view === 'week' ? <WeekView {...viewProps} /> : <MonthView {...viewProps} />}
      {selectedInRange && calendar.data && (
        <DayPanel
          date={selectedInRange}
          items={itemsByDay(calendar.data.items).get(selectedInRange) ?? []}
          seconds={calendar.data.days?.find((d) => d.date === selectedInRange)?.seconds ?? null}
          today={today}
          onClose={() => setSelected(null)}
        />
      )}
      {view === 'week' && summary && <NeedsYouBar data={summary} onShow={onShowDay} />}
    </div>
  );
}

function TodayLists({ data, pending, today, now }: { data: TodaySummary; pending: boolean; today: string; now: Date }) {
  const access = useAccess();
  const phone = useIsPhone();
  // spotkania dnia (kalendarz iCal i moduł Spotkania; moduł Klienci) do „Plan na dziś”
  const calendar = useTodayCalendar({ from: today, to: today }, { enabled: access.can('clients') });
  const meetings = (itemsByDay(calendar.data?.items ?? []).get(today) ?? []).filter((i) => i.kind === 'meeting');
  const meetingsPending = access.can('clients') && calendar.isPending;
  return (
    // białe sekcje (DECISIONS 2026-10-07); dwie kolumny od 1280 px: przy 768–1279 px kolumna miałaby ~200–340 px i tytuły znikałyby za statusem i klientem
    <div className="mt-6 grid gap-4 md:gap-6 xl:grid-cols-2">
      <Section aria-labelledby="needs-you">
        <SectionHeader title={<span id="needs-you">Wymaga Ciebie</span>} />
        {pending ? (
          <ListSkeleton rows={3} />
        ) : data.needsYou.length === 0 ? (
          <EmptyState compact icon={CircleCheck} title="Nic nie czeka na Twoją decyzję." />
        ) : (
          data.needsYou.map((item) => (
            <ListRow
              key={item.id}
              to={item.to}
              leading={<Dot tone={item.tone} />}
              title={item.title}
              // telefon: „od 07:59” i „Symulacja” pod tytułem (obok tytuł łamał się na 2–3 linie)
              description={
                phone && (item.meta || item.simulated)
                  ? [item.meta, item.simulated ? 'Symulacja' : null].filter(Boolean).join(' · ')
                  : undefined
              }
              meta={phone ? undefined : item.meta}
              trailing={!phone && item.simulated ? <Status tone="warning">Symulacja</Status> : undefined}
              chevron
            />
          ))
        )}
      </Section>

      <PlanToday
        tasks={data.plan}
        meetings={meetings}
        pending={pending || meetingsPending}
        nowTime={toLocalTime(now)}
      />
    </div>
  );
}

function TodayMeta({ date, data, canApprovals }: { date: Date; data?: TodaySummary; canApprovals: boolean }) {
  const parts = [formatDayLong(date)];
  if (data) {
    const extra: string[] = [];
    if (canApprovals && data.approvals.pending > 0) {
      const n = data.approvals.pending;
      extra.push(`${n} ${plural(n, ['akcja czeka', 'akcje czekają', 'akcji czeka'])} na akceptację`);
    }
    if (data.alarms.open > 0) {
      extra.push(`${data.alarms.open} ${plural(data.alarms.open, ['alarm', 'alarmy', 'alarmów'])}`);
    }
    if (extra.length > 0) parts.push(extra.join(', '));
  }
  return <>{parts.join(' · ')}</>;
}

function TodayStats({ data, now }: { data: TodaySummary; now: Date }) {
  const access = useAccess();
  const tiles = [];

  if (access.can('approvals')) {
    const pending = data.approvals.pending;
    tiles.push(
      <Stat
        key="approvals"
        to="/akceptacje"
        label="Do akceptacji"
        icon={ShieldCheck}
        value={pending}
        highlight={pending > 0}
        tone="accent"
        showDot={pending > 0}
        note={
          pending > 0 && data.approvals.oldestWaitingSec !== null
            ? `najstarsza czeka ${formatAge(data.approvals.oldestWaitingSec)}`
            : 'nic nie czeka'
        }
      />,
    );
  }
  if (access.can('tasks')) {
    tiles.push(
      <Stat
        key="tasks"
        to="/zadania"
        label="Zadania na dziś"
        icon={SquareCheckBig}
        value={data.tasks.today}
        tone={data.tasks.overdue > 0 ? 'warning' : 'neutral'}
        showDot={data.tasks.overdue > 0}
        note={data.tasks.overdue > 0 ? `${data.tasks.overdue} po terminie` : 'nic po terminie'}
      />,
    );
  }
  if (access.can('maintenance')) {
    // otwarte alarmy = incydenty monitoringu + inne „Pilne” (kompozycja Fali 4): jeden incydent → jego widok; więcej
    // — z modułem Inbox przefiltrowana lista „Pilne”, bez niego Utrzymanie
    const alarmsTo =
      data.alarms.open === 1 && data.alarms.firstIncidentId
        ? incidentPath(data.alarms.firstIncidentId)
        : access.can('inbox') && data.alarms.open > 0
          ? '/inbox?waznosc=critical'
          : '/utrzymanie';
    tiles.push(
      <Stat
        key="alarms"
        to={alarmsTo}
        label="Alarmy"
        icon={TriangleAlert}
        value={data.alarms.open}
        tone={data.alarms.open > 0 ? 'danger' : 'success'}
        note={
          data.alarms.open > 0
            ? `${data.alarms.firstSimulated ? 'Symulacja: ' : ''}${data.alarms.firstTitle ?? 'sprawdź Utrzymanie'}`
            : 'wszystko działa'
        }
      />,
    );
  }
  if (data.partnerPayout) {
    // Przegląd Fali 11: partner (bez Rozliczeń, ze stawką partnera) — „Do wypłaty” zamiast „Niezafakturowane”
    // (fakturowanie klienta to sprawa prowadzącego; ta sama definicja co „Do wypłaty” w Czasie)
    const pay = data.partnerPayout;
    tiles.push(
      <Stat
        key="unbilled"
        to="/czas?widok=rozliczenia"
        label="Do wypłaty"
        icon={Wallet}
        value={formatMoneyAuto(pay.amount, pay.currency)}
        showDot={false}
        note={
          pay.entries === 0
            ? 'wszystko rozliczone'
            : `${formatDuration(pay.seconds)} nierozliczone${pay.since ? ` · od ${formatDateShort(pay.since, now)}` : ''}`
        }
      />,
    );
  } else if (access.can('billing') || access.can('time')) {
    const unbilled = data.unbilled;
    const showAmount = access.flag('seesRates') && unbilled !== null && unbilled.amount !== null;
    tiles.push(
      <Stat
        key="unbilled"
        to={access.can('billing') ? '/rozliczenia' : '/czas'}
        label="Niezafakturowane"
        icon={Wallet}
        value={
          unbilled === null
            ? '—'
            : showAmount
              ? formatMoneyTotal({ ...unbilled, amount: unbilled.amount ?? '0' }, { short: true })
              : formatDuration(unbilled.seconds)
        }
        showDot={false}
        note={
          unbilled === null
            ? 'policzymy po wpisach czasu'
            : unbilled.entries === 0
              ? 'wszystko rozliczone'
              : showAmount
                ? formatDuration(unbilled.seconds)
                : 'czasu do rozliczenia'
        }
      />,
    );
  }
  if (access.can('clients')) {
    const meeting = data.nextMeeting;
    tiles.push(
      <Stat
        key="meeting"
        label="Najbliższe spotkanie"
        icon={Calendar}
        fitValue
        to={meeting ? `/spotkania/${meeting.id}` : '/spotkania'}
        value={meeting ? `${formatDateShort(meeting.startsAt, now)} · ${formatTime(meeting.startsAt)}` : '—'}
        showDot={false}
        note={
          meeting
            ? // Figma 01: „czwartek · REA, przegląd FAQ”
              `${formatWeekday(meeting.startsAt)} · ${[meeting.clientName, lowerInitial(meeting.title)].filter(Boolean).join(', ')}`
            : 'brak zaplanowanych'
        }
      />,
    );
  }
  if (tiles.length === 0) {
    tiles.push(
      <Stat key="inbox" label="Dziś" icon={Inbox} value="—" showDot={false} note="brak modułów z podsumowaniem" />,
    );
  }
  // Mobile (2 kolumny): „Najbliższe spotkanie” na pełną szerokość, jak w Figmie (profil klienta, mobile),
  // bo data i godzina nie mieszczą się w połowie ekranu.
  return tiles.map((tile) => (
    <div key={tile.key} className={cn('min-w-0 [&>*]:h-full', tile.key === 'meeting' && 'col-span-2 md:col-span-1')}>
      {tile}
    </div>
  ));
}
